import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePpjkEvent } from '@/features/realtime/ppjkEvents';
import ColleagueFilledPanel from './ColleagueFilledPanel';
import { createOwnWriteGuard } from '@/features/consignee-import/ownWriteGuard';
import { useMediaQuery } from '@mantine/hooks';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { useLanguage } from '@contexts/LanguageContext';
import { useSidebarIdentity, useSidebarMenus } from '@components/sidebar/sidebar-v2';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/lumen/dialog';
import { cn } from '@/lib/utils';
import { MODULE_BY_SIDEBAR_LABEL } from '@constants/modules';
import type { ImportResult } from '@/features/consignee-import/types';
import { useUpdateOnboarding } from './api';
import { useMyStatementConfigs } from '@/features/statement-configs/api';
import { statementConfigKeys } from '@/features/statement-configs/keys';
import { resolveFirstLoginCopy } from './i18n';
import StepConsignee from './steps/StepConsignee';
import StepFilm from './steps/StepFilm';
import StepPernyataan, { type StepPernyataanHandle } from './steps/StepPernyataan';
import StepTampilan from './steps/StepTampilan';
import {
  colleagueConsigneePanelVisible,
  colleagueStatementPanelVisible,
  dataStepsShownIn,
  filmCopyFor,
  filmVariantFor,
  nextStepAfter,
  pickUtamaStatement,
  railNoteFor,
  resolveAfterRefresh,
  STEP_ORDER,
  stepsFor,
  type OnboardingState,
  type OnboardingStepKey,
} from './types';

import { ONBOARDING_MODAL_Z_INDEX } from './constants';

/** Lumen dialogs close on these by default; the wizard closes only through its footer. */
const keepOpen = (event: Event) => event.preventDefault();

export interface OnboardingWizardProps {
  opened: boolean;
  state: OnboardingState;
  /** Re-reads the server state; used before a data step renders so a colleague's save is honoured. */
  refresh: () => Promise<OnboardingState | undefined>;
  /** "Lanjutkan nanti": the wizard closes and will not auto-open again. */
  onLater: () => void;
  /** Every visible step handled. */
  onComplete: () => void;
  /** False for a help-menu replay by a non-pending user: no onboarding PUTs at all. */
  recordProgress?: boolean;
  zIndex?: number;
}

/**
 * The first-login wizard, a full-screen Lumen Dialog. Owns which step is on
 * screen and the per-step server bookkeeping; the steps own their forms. The
 * left rail is dark like the shell's rail, so the wizard reads as part of the
 * app, not a marketing overlay. Mantine inputs inside the steps render through
 * the dialog's Mantine layer host (overlay-coexistence.tsx).
 */
export const OnboardingWizard = ({
  opened,
  state: initialState,
  refresh,
  onLater,
  onComplete,
  recordProgress = true,
  zIndex = ONBOARDING_MODAL_Z_INDEX,
}: OnboardingWizardProps) => {
  const { language } = useLanguage();
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => resolveFirstLoginCopy(language, key, vars),
    [language]
  );
  const { userEmail, professionalModeEnabled } = useSidebarIdentity();
  const { filteredMenus, isFeatureEnabled } = useSidebarMenus({ userEmail, professionalModeEnabled });
  // In the menu AND not module-locked: a locked Master row stays visible but
  // dimmed, and neither data page under /master would open (see StepContext).
  // The film only needs the Master group; each data step needs its own page.
  // A locked Master row stays visible but dimmed, and neither page would open.
  const reachability = useMemo(() => {
    const masterGroup = filteredMenus.find((item) => item.label === 'Master');
    const moduleCode = MODULE_BY_SIDEBAR_LABEL.Master;
    const enabled = moduleCode ? isFeatureEnabled(moduleCode) : true;
    const group = Boolean(masterGroup) && enabled;
    // subLinks are already filtered by role menus and the Master allowlist.
    const hasPage = (link: string) => group && Boolean(masterGroup?.subLinks?.some((sub) => sub.link === link));
    return {
      master: group,
      statement: hasPage('/master/statement'),
      consignee: hasPage('/master/consignee'),
    };
  }, [filteredMenus, isFeatureEnabled]);
  const masterReachable = reachability.master;
  const narrow = useMediaQuery('(max-width: 767px)');
  const update = useUpdateOnboarding();

  // The gate mounts a fresh wizard per run, so initial values are the run's values.
  const [state, setState] = useState(initialState);
  // Decided at open (the gate waits for company features and permissions
  // first) so the step list and the film cannot disagree later in the run.
  const [reachableAtOpen] = useState(reachability);
  const [filmVariant] = useState(() => filmVariantFor(initialState, masterReachable));
  const [currentKey, setCurrentKey] = useState<OnboardingStepKey>('tampilan');
  // Steps the user has actually seen this run. A data step drops out of
  // `stepsFor` the moment its data exists (that is how a colleague's save
  // removes it), but a step the user just completed must stay on the rail
  // as done rather than vanish.
  const [shownKeys, setShownKeys] = useState<OnboardingStepKey[]>(['tampilan']);
  // Steps already recorded as done this run. Revisiting one via Kembali must
  // not turn it into 'skipped' and must not re-record it unchanged.
  const [doneKeys, setDoneKeys] = useState<OnboardingStepKey[]>([]);
  // Reported by the Pernyataan form; decides "Lanjut" vs "Simpan & lanjut" on a revisit.
  const [pernyataanDirty, setPernyataanDirty] = useState(true);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  // Realtime (contract §8): a colleague may fill a data step while this user
  // is in the wizard. These track what this user did, so the notice appears
  // only for someone else's change.
  const [statementSavedThisRun, setStatementSavedThisRun] = useState(false);
  const [statementPanelDismissed, setStatementPanelDismissed] = useState(false);
  const [consigneePanelDismissed, setConsigneePanelDismissed] = useState(false);
  // This user's own import: in flight, and the echo window after it succeeds.
  const ownImport = useRef(createOwnWriteGuard()).current;
  // Same for this user's Pernyataan saves (the step's own save, "Timpa" included).
  const ownStatement = useRef(createOwnWriteGuard()).current;
  // The step's 409 panel is open: the colleague notice would say the same twice.
  const [statementConflictOpen, setStatementConflictOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const queryClient = useQueryClient();
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const pernyataanRef = useRef<StepPernyataanHandle>(null);

  const context = useMemo(
    () => ({ statementReachable: reachableAtOpen.statement, consigneeReachable: reachableAtOpen.consignee }),
    [reachableAtOpen]
  );
  const steps = useMemo(() => stepsFor(state, context), [state, context]);
  const railSteps = useMemo(
    () => STEP_ORDER.filter((key) => steps.includes(key) || shownKeys.includes(key)),
    [steps, shownKeys]
  );
  const currentIndex = Math.max(0, railSteps.indexOf(currentKey));
  const filmCopy = filmCopyFor(filmVariant, dataStepsShownIn(railSteps), state);

  const record = useCallback(
    async (key: OnboardingStepKey, result: 'done' | 'skipped') => {
      // A step recorded as done stays done: nothing to send again.
      if (doneKeys.includes(key)) return;
      if (!recordProgress) {
        // Replay: the screen behaves the same, the server hears nothing.
        if (result === 'done') setDoneKeys((previous) => [...previous, key]);
        return;
      }
      try {
        await update.mutateAsync({ steps: { [key]: result } });
        if (result === 'done') setDoneKeys((previous) => [...previous, key]);
      } catch (error) {
        // Bookkeeping only; the user's choice already took effect on screen.
        console.warn('[first-login] step record failed', error);
      }
    },
    [update, doneKeys, recordProgress]
  );

  /**
   * Moves past `from`, honouring anything a colleague filled in the meantime.
   * A refetch that did not genuinely answer (flagged `unavailable`, or a
   * thrown error) leaves the current state and step list untouched: a blip
   * must never jump the user past steps they have not seen.
   */
  const advance = useCallback(
    async (from: OnboardingStepKey) => {
      let latest = state;
      try {
        latest = resolveAfterRefresh(state, await refresh());
      } catch (error) {
        console.warn('[first-login] refresh failed; continuing with the last known state', error);
      }
      if (latest !== state) setState(latest);
      const next = nextStepAfter(from, latest, context);
      if (!next) {
        onComplete();
        return;
      }
      setShownKeys((previous) => (previous.includes(next) ? previous : [...previous, next]));
      setCurrentKey(next);
    },
    [state, refresh, context, onComplete]
  );

  const handleSkip = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await record(currentKey, 'skipped');
      await advance(currentKey);
    } finally {
      setBusy(false);
    }
  };

  const handlePrimary = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (currentKey === 'pernyataan') {
        // Already saved and untouched since: nothing to send.
        const untouched = doneKeys.includes('pernyataan') && !pernyataanDirty;
        if (!untouched) {
          const saved = await pernyataanRef.current?.save();
          if (!saved) return;
          setStatementSavedThisRun(true);
        }
      }
      await record(currentKey, 'done');
      if (currentKey === 'film') {
        onComplete();
        return;
      }
      await advance(currentKey);
    } finally {
      setBusy(false);
    }
  };

  /** Back to the previous step on the rail. Records nothing; forms keep their values. */
  const handleBack = () => {
    if (busy) return;
    const previous = railSteps[railSteps.indexOf(currentKey) - 1];
    if (!previous) return;
    setCurrentKey(previous);
    // Kembali unmounts itself on the first step; keep focus in the footer.
    window.requestAnimationFrame(() => primaryRef.current?.focus());
  };

  /** Re-reads the state for a realtime event; a failed read changes nothing. */
  const refreshFromEvent = useCallback(async () => {
    try {
      const fresh = await refresh();
      setState((previous) => resolveAfterRefresh(previous, fresh));
    } catch (error) {
      console.warn('[first-login] refresh after realtime event failed', error);
    }
  }, [refresh]);

  usePpjkEvent('statement_config_changed', () => {
    void queryClient.invalidateQueries({ queryKey: statementConfigKeys.all });
    void refreshFromEvent();
    // Our own save's broadcast is not a colleague's change.
    if (ownStatement.isSuppressed()) return;
    setStatementPanelDismissed(false);
  });

  const handleStatementSaveState = (saveState: 'start' | 'success' | 'error') => {
    if (saveState === 'start') ownStatement.begin();
    else if (saveState === 'success') ownStatement.succeed();
    else ownStatement.fail();
  };
  usePpjkEvent('consignee_master_changed', () => {
    // Our own import's broadcast is not a colleague's change.
    if (ownImport.isSuppressed()) return;
    setConsigneePanelDismissed(false);
    void refreshFromEvent();
  });

  const handleApplyStateChange = (applyState: 'start' | 'success' | 'error') => {
    if (applyState === 'start') {
      ownImport.begin();
      setImporting(true);
      return;
    }
    if (applyState === 'success') ownImport.succeed();
    else ownImport.fail();
    setImporting(false);
  };

  /** "Lanjut" on a colleague panel: the step counts as done and the wizard moves on. */
  const handleColleagueContinue = async (step: OnboardingStepKey) => {
    if (busy) return;
    setBusy(true);
    try {
      await record(step, 'done');
      await advance(step);
    } finally {
      setBusy(false);
    }
  };

  const handleImported = (result: ImportResult) => {
    setImportedCount(result.imported.new + result.imported.updated);
  };

  const showStatementPanel =
    shownKeys.includes('pernyataan') &&
    !statementConflictOpen &&
    colleagueStatementPanelVisible({
      hasStatement: state.hasStatement,
      savedThisRun: statementSavedThisRun,
      dismissed: statementPanelDismissed,
      saving: busy && currentKey === 'pernyataan',
    });
  const showConsigneePanel =
    shownKeys.includes('consignee') &&
    colleagueConsigneePanelVisible({
      consigneeCount: state.consigneeCount,
      importedThisRun: importedCount !== null,
      dismissed: consigneePanelDismissed,
      importing,
    });
  const colleagueStatement = pickUtamaStatement(useMyStatementConfigs(showStatementPanel).data);

  const currentDone = doneKeys.includes(currentKey);
  const primaryLabel =
    currentKey === 'film'
      ? t('footer.finish')
      : currentKey === 'consignee'
        ? t('footer.next')
        : currentKey === 'tampilan'
          ? t('footer.next')
          : currentDone && !pernyataanDirty
            ? t('footer.next')
            : t('footer.saveNext');
  const primaryDisabled = currentKey === 'consignee' && importedCount === null;
  // A step already recorded as done offers no Lewati: skipping it would
  // overwrite 'done' with 'skipped' while its data exists.
  const showSkip = currentKey !== 'film' && !currentDone;
  const showBack = railSteps.indexOf(currentKey) > 0;

  const railNote =
    currentKey === 'consignee'
      ? t('rail.note.consignee')
      : currentKey === 'pernyataan'
        ? t('rail.note.private')
        : t(`rail.note.${railNoteFor(filmCopy)}`);

  // Phones: smaller buttons, and the footer wraps rather than pushing the primary off screen.
  const buttonSize = narrow ? 'sm' : 'default';

  const stepList = (
    <ol className={cn('flex flex-wrap', narrow ? 'flex-row gap-3.5' : 'flex-col gap-1')}>
      {railSteps.map((key, index) => {
        const isCurrent = key === currentKey;
        const isDone = index < currentIndex;
        return (
          <li
            key={key}
            aria-current={isCurrent ? 'step' : undefined}
            className={cn(
              'flex items-start gap-2.5',
              narrow ? 'p-0' : 'py-2',
              isCurrent ? 'text-fg' : isDone ? 'text-fg-2' : 'text-fg-muted'
            )}
          >
            <span
              className={cn(
                'mt-px grid size-5.5 flex-none place-items-center rounded-full border-[1.5px] font-semibold text-[11px]',
                isCurrent
                  ? 'border-transparent bg-grad-btn text-white'
                  : isDone
                    ? 'border-fg-2 bg-fg-2 text-canvas'
                    : 'border-line-control text-fg-muted'
              )}
            >
              {isDone ? <CheckIcon className="size-3" strokeWidth={2.5} aria-hidden="true" /> : index}
            </span>
            <div className="min-w-0">
              <div className={cn('text-[13.5px] leading-snug', isCurrent ? 'font-semibold' : 'font-medium')}>
                {t(key === 'film' ? `step.film.${filmCopy}.title` : `step.${key}.title`)}
              </div>
              {!narrow && <div className="text-fg-muted text-xs">{t(`step.${key}.sub`)}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );

  return (
    // Controlled by the gate only: no Radix dismissal path reaches onOpenChange.
    <Dialog open={opened}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        onEscapeKeyDown={keepOpen}
        onPointerDownOutside={keepOpen}
        onInteractOutside={keepOpen}
        data-onboarding-wizard=""
        // Lumen layers sit at z-500; the wizard keeps its Mantine tier so the
        // patch-log modal (9000) stays above it. Full screen and opaque, so
        // the dialog's own scrim at z-500 underneath never shows.
        style={{ zIndex }}
        className={cn(
          'm-0 flex h-dvh max-h-none w-full max-w-none gap-0 overflow-hidden rounded-none border-0 bg-canvas p-0 shadow-none sm:max-w-none',
          'data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100',
          narrow ? 'flex-col' : 'flex-row'
        )}
      >
        {/* The rail reads as the app's own sidebar: dark in both modes, like the shell's rail. */}
        <aside
          className={cn(
            'lm-dark flex flex-none flex-col overflow-y-auto bg-canvas text-fg',
            narrow ? 'w-full gap-3 border-line border-b px-5 py-4' : 'w-75 gap-4.5 border-line border-r px-6 py-6.5'
          )}
        >
          <div>
            <DialogTitle className="font-bold text-fg text-xl leading-tight">{t('rail.welcome')}</DialogTitle>
            <p className="mt-0.5 text-fg-muted text-xs">
              {t('footer.stepOf', { current: currentIndex + 1, total: railSteps.length })}
            </p>
          </div>
          {stepList}
          {!narrow && <p className="mt-auto text-fg-muted text-xs leading-relaxed">{railNote}</p>}
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div
            data-onboarding-body
            // No bottom padding: a sticky bar inside pins to the scrollport's
            // padding box, so padding here would float it above the visible
            // edge. The trailing spacer below gives the last row its room.
            className={cn('min-h-0 flex-1 overflow-y-auto', narrow ? 'px-4 pt-5' : 'px-10 pt-8')}
          >
            <div className="mx-auto max-w-220">
              {currentKey === 'tampilan' && <StepTampilan />}
              {/* The form steps stay mounted once visited so Kembali brings back
                  what was typed or previewed; the film restarts on every visit. */}
              {shownKeys.includes('pernyataan') && (
                <div hidden={currentKey !== 'pernyataan'}>
                  {showStatementPanel && (
                    <ColleagueFilledPanel
                      title={t('colleague.statement.title')}
                      details={
                        colleagueStatement
                          ? [
                              { label: t('pernyataan.field.place'), value: colleagueStatement.place },
                              { label: t('pernyataan.field.name'), value: colleagueStatement.name },
                              { label: t('pernyataan.field.position'), value: colleagueStatement.position },
                            ]
                          : undefined
                      }
                      continueLabel={t('colleague.continue')}
                      keepLabel={t('colleague.statement.keep')}
                      onContinue={() => void handleColleagueContinue('pernyataan')}
                      onKeep={() => {
                        // "Tetap ubah": edit on top of the colleague's Utama, so
                        // the save expects their updatedAt (a 409 otherwise).
                        if (colleagueStatement) pernyataanRef.current?.adoptCurrent(colleagueStatement);
                        setStatementPanelDismissed(true);
                      }}
                      busy={busy}
                    />
                  )}
                  <StepPernyataan
                    ref={pernyataanRef}
                    onDirtyChange={setPernyataanDirty}
                    onSaveStateChange={handleStatementSaveState}
                    onSaved={() => setStatementSavedThisRun(true)}
                    onConflictChange={setStatementConflictOpen}
                  />
                </div>
              )}
              {shownKeys.includes('consignee') && (
                <div hidden={currentKey !== 'consignee'}>
                  {showConsigneePanel && (
                    <ColleagueFilledPanel
                      title={t('colleague.consignee.title', { n: state.consigneeCount })}
                      continueLabel={t('colleague.continue')}
                      keepLabel={t('colleague.consignee.keep')}
                      onContinue={() => void handleColleagueContinue('consignee')}
                      onKeep={() => setConsigneePanelDismissed(true)}
                      busy={busy}
                    />
                  )}
                  <StepConsignee
                    onImported={handleImported}
                    importedCount={importedCount}
                    onApplyStateChange={handleApplyStateChange}
                  />
                </div>
              )}
              {currentKey === 'film' && <StepFilm variant={filmVariant} copy={filmCopy} />}
              <div className={narrow ? 'h-10' : 'h-14'} aria-hidden="true" />
            </div>
          </div>
          <div
            data-onboarding-footer
            className={cn(
              'flex flex-none flex-wrap items-center justify-between border-line border-t bg-raised',
              narrow ? 'gap-2 px-4 py-3' : 'gap-3 px-10 py-3.5'
            )}
          >
            <Button variant="ghost" size={buttonSize} onClick={onLater} disabled={busy}>
              {t('footer.later')}
            </Button>
            <div className="ml-auto flex flex-wrap justify-end gap-2">
              {showBack && (
                <Button variant="ghost" size={buttonSize} onClick={handleBack} disabled={busy}>
                  {t('footer.back')}
                </Button>
              )}
              {showSkip && (
                <Button variant="outline" size={buttonSize} onClick={handleSkip} disabled={busy}>
                  {t('footer.skip')}
                </Button>
              )}
              <Button
                ref={primaryRef}
                size={buttonSize}
                onClick={handlePrimary}
                disabled={primaryDisabled || busy}
                aria-busy={busy || undefined}
              >
                {busy && <Loader2Icon className="animate-spin" aria-hidden="true" />}
                {primaryLabel}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
